import Phaser from 'phaser'
import type { Tower, Creep, TowerType, Point, Projectile, GameState, SendUnitType } from '@shared/types'
import {
  GRID_WIDTH,
  GRID_HEIGHT,
  TILE_SIZE,
  TOWERS,
  SEND_UNITS,
  COLORS,
  STARTING_GOLD,
  STARTING_LIVES,
  BASE_INCOME,
  BUILD_TIME_SECONDS,
  getWaveConfig
} from '@shared/constants'
import { findPath, canPlaceTower, createEmptyGrid } from '@shared/pathfinding'
import { getNetworkClient, NetworkClient } from '../NetworkClient'
import { IS_MOBILE } from '../main'

interface GameSceneData {
  mode: 'single' | 'multi'
  roomId?: string
  playerId?: string
}

interface FloatingText {
  text: Phaser.GameObjects.Text
  vy: number
  life: number
}

interface GameStats {
  towersBuilt: number
  unitsSent: number
  creepsKilled: number
  goldEarned: number
  damageDealt: number
}

// Data structure for each enemy lane (supports up to 3 enemies)
interface EnemyLaneData {
  playerId: string
  grid: boolean[][]
  towers: Tower[]
  creeps: Creep[]
  path: Point[]
  lives: number
  gold: number
  income: number
  laneX: number
  laneY: number
  tileSize: number
  graphics: {
    grid: Phaser.GameObjects.Graphics
    path: Phaser.GameObjects.Graphics
    tower: Phaser.GameObjects.Graphics
    creep: Phaser.GameObjects.Graphics
  }
  livesText?: Phaser.GameObjects.Text
}

export class GameScene extends Phaser.Scene {
  private mode: 'single' | 'multi' = 'single'
  private roomId: string = ''
  private playerId: string = ''
  private network: NetworkClient | null = null

  // Grid and lanes
  private playerGrid: boolean[][] = []
  private playerTowers: Tower[] = []
  private playerCreeps: Creep[] = []
  private playerPath: Point[] = []

  // Enemy lanes (supports multiple enemies)
  private enemies: Map<string, EnemyLaneData> = new Map()

  // Game state
  private gold: number = STARTING_GOLD
  private income: number = BASE_INCOME
  private lives: number = STARTING_LIVES
  private currentWave: number = 0
  private isWaveActive: boolean = false
  private buildTimeRemaining: number = BUILD_TIME_SECONDS
  private stats: GameStats = { towersBuilt: 0, unitsSent: 0, creepsKilled: 0, goldEarned: 0, damageDealt: 0 }

  // Selection
  private selectedTowerType: TowerType | null = null
  private hoverTile: Point | null = null
  private hoveredTower: Tower | null = null

  // Layout - dynamic based on screen
  private PLAYER_LANE_X = 20
  private PLAYER_LANE_Y = 70
  private PLAYER_TILE_SIZE = 40
  private SIDEBAR_X = 860
  private SIDEBAR_Y = 70
  private SIDEBAR_WIDTH = 200
  private ENEMY_TILE_SIZE = 14
  private UI_PANEL_Y = 490
  private isMobile = false
  private BUTTON_SCALE = 1

  // Player colors for scoreboard
  private PLAYER_COLORS = [0x4a90d9, 0xd94a4a, 0x4ad94a, 0xd9d94a]

  // Graphics layers
  private playerGridGraphics!: Phaser.GameObjects.Graphics
  private playerPathGraphics!: Phaser.GameObjects.Graphics
  private playerTowerGraphics!: Phaser.GameObjects.Graphics
  private playerCreepGraphics!: Phaser.GameObjects.Graphics
  private projectileGraphics!: Phaser.GameObjects.Graphics
  private uiOverlayGraphics!: Phaser.GameObjects.Graphics

  // For backward compat in single player - primary enemy
  private get primaryEnemy(): EnemyLaneData | undefined {
    return this.enemies.values().next().value
  }

  // UI Elements
  private goldText!: Phaser.GameObjects.Text
  private incomeText!: Phaser.GameObjects.Text
  private waveText!: Phaser.GameObjects.Text
  private scoreboardContainer!: Phaser.GameObjects.Container
  private timerText!: Phaser.GameObjects.Text
  private waveInfoText!: Phaser.GameObjects.Text
  private tooltipContainer!: Phaser.GameObjects.Container
  private tooltipBg!: Phaser.GameObjects.Rectangle
  private tooltipText!: Phaser.GameObjects.Text
  private readyButton!: Phaser.GameObjects.Container
  private towerButtons: Map<TowerType, Phaser.GameObjects.Container> = new Map()
  private sendButtons: Map<string, Phaser.GameObjects.Container> = new Map()

  // Projectiles and effects
  private projectiles: Projectile[] = []
  private floatingTexts: FloatingText[] = []

  // Timers
  private buildTimer?: Phaser.Time.TimerEvent
  private creepsToSpawn: number = 0
  private enemyCreepsToSpawn: Map<string, number> = new Map()
  private activeTimers: Phaser.Time.TimerEvent[] = []

  constructor() {
    super({ key: 'GameScene' })
  }

  init(data: GameSceneData) {
    this.mode = data.mode || 'single'
    this.roomId = data.roomId || ''
    this.playerId = data.playerId || ''
    this.network = null
    this.resetState()
  }

  private resetState() {
    this.playerTowers = []
    this.playerCreeps = []
    this.projectiles = []
    this.floatingTexts = []
    this.gold = STARTING_GOLD
    this.income = BASE_INCOME
    this.lives = STARTING_LIVES
    this.currentWave = 0
    this.isWaveActive = false
    this.selectedTowerType = null
    this.hoverTile = null
    this.hoveredTower = null
    this.stats = { towersBuilt: 0, unitsSent: 0, creepsKilled: 0, goldEarned: 0, damageDealt: 0 }
    this.activeTimers = []
    this.enemies.clear()
    this.enemyCreepsToSpawn.clear()
  }

  create() {
    this.detectMobile()
    this.calculateLayout()
    this.initializeGrids()
    this.createGraphicsLayers()
    this.createUI()
    this.createTooltip()
    this.setupInput()
    this.drawStaticElements()

    // Register shutdown handler for cleanup
    this.events.on('shutdown', this.cleanup, this)

    if (this.mode === 'multi') {
      this.setupMultiplayer()
    } else {
      this.startBuildPhase()
    }
  }

  private cleanup() {
    // Clean up network handlers
    if (this.network) {
      this.network.clearAllHandlers()
    }

    // Clean up active timers
    for (const timer of this.activeTimers) {
      timer.destroy()
    }
    this.activeTimers = []

    if (this.buildTimer) {
      this.buildTimer.destroy()
      this.buildTimer = undefined
    }

    // Clean up floating texts
    for (const ft of this.floatingTexts) {
      ft.text.destroy()
    }
    this.floatingTexts = []

    // Clean up player graphics
    this.playerGridGraphics?.destroy()
    this.playerPathGraphics?.destroy()
    this.playerTowerGraphics?.destroy()
    this.playerCreepGraphics?.destroy()

    // Clean up enemy graphics
    for (const enemy of this.enemies.values()) {
      enemy.graphics.grid?.destroy()
      enemy.graphics.path?.destroy()
      enemy.graphics.tower?.destroy()
      enemy.graphics.creep?.destroy()
      enemy.livesText?.destroy()
    }
    this.enemies.clear()

    this.projectileGraphics?.destroy()
    this.uiOverlayGraphics?.destroy()
  }

  private detectMobile() {
    // Use centralized mobile detection from main.ts (based on actual screen size)
    this.isMobile = IS_MOBILE
  }

  private calculateLayout() {
    const width = this.cameras.main.width
    const height = this.cameras.main.height

    if (this.isMobile) {
      // Mobile layout - focus on player lane, sidebar hidden or minimal
      this.PLAYER_TILE_SIZE = 32
      this.ENEMY_TILE_SIZE = 10
      this.PLAYER_LANE_X = 10
      this.PLAYER_LANE_Y = 60
      this.SIDEBAR_X = this.PLAYER_LANE_X + GRID_WIDTH * this.PLAYER_TILE_SIZE + 10
      this.SIDEBAR_Y = 60
      this.SIDEBAR_WIDTH = 140
      this.UI_PANEL_Y = this.PLAYER_LANE_Y + GRID_HEIGHT * this.PLAYER_TILE_SIZE + 10
      this.BUTTON_SCALE = 1.1
    } else {
      // Desktop layout - player lane with sidebar for enemies
      this.PLAYER_TILE_SIZE = 40
      this.ENEMY_TILE_SIZE = 14
      this.PLAYER_LANE_X = 20
      this.PLAYER_LANE_Y = 70
      this.SIDEBAR_X = this.PLAYER_LANE_X + GRID_WIDTH * this.PLAYER_TILE_SIZE + 40
      this.SIDEBAR_Y = 70
      this.SIDEBAR_WIDTH = 220
      this.UI_PANEL_Y = 490
      this.BUTTON_SCALE = 1
    }
  }

  private setupMultiplayer() {
    try {
      this.network = getNetworkClient()

      this.network.on('onGameState', (state: GameState) => {
        this.syncGameState(state)
      })

      this.network.on('onTowerPlaced', (playerId: string, tower: Tower) => {
        if (playerId === this.playerId) {
          // Our tower was placed
          this.playerTowers.push(tower)
          this.playerGrid[tower.y][tower.x] = false
          this.playerPath = findPath(this.playerGrid, { x: 0, y: 0 }, { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }) || []
        } else {
          // Enemy placed a tower - find or create their lane
          let enemy = this.enemies.get(playerId)
          if (!enemy) {
            enemy = this.createEnemyLane(playerId, this.enemies.size)
            this.enemies.set(playerId, enemy)
          }
          enemy.towers.push(tower)
          enemy.grid[tower.y][tower.x] = false
          enemy.path = findPath(enemy.grid, { x: 0, y: 0 }, { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }) || []
        }
        this.drawStaticElements()
      })

      this.network.on('onTowerRejected', (reason: string) => {
        this.createFloatingText(640, 300, reason, '#ff4444')
      })

      this.network.on('onCreepSpawned', (creep: Creep) => {
        if (creep.laneId === this.playerId) {
          this.playerCreeps.push(creep)
        } else {
          // Add to appropriate enemy lane
          const enemy = this.enemies.get(creep.laneId)
          if (enemy) {
            enemy.creeps.push(creep)
          }
        }
      })

      this.network.on('onCreepUpdate', (creepId: string, x: number, y: number, hp: number) => {
        // Check player creeps
        let creep = this.playerCreeps.find(c => c.id === creepId)
        if (creep) {
          creep.x = x
          creep.y = y
          creep.hp = hp
          return
        }
        // Check enemy creeps
        for (const enemy of this.enemies.values()) {
          creep = enemy.creeps.find(c => c.id === creepId)
          if (creep) {
            creep.x = x
            creep.y = y
            creep.hp = hp
            return
          }
        }
      })

      this.network.on('onCreepDied', (creepId: string, killerId: string, bounty: number) => {
        this.playerCreeps = this.playerCreeps.filter(c => c.id !== creepId)
        for (const enemy of this.enemies.values()) {
          enemy.creeps = enemy.creeps.filter(c => c.id !== creepId)
        }
        if (bounty > 0) {
          this.stats.creepsKilled++
        }
      })

      this.network.on('onCreepEscaped', (creepId: string, laneId: string) => {
        this.playerCreeps = this.playerCreeps.filter(c => c.id !== creepId)
        for (const enemy of this.enemies.values()) {
          enemy.creeps = enemy.creeps.filter(c => c.id !== creepId)
        }
      })

      this.network.on('onWaveStart', (wave: number, buildTime: number) => {
        this.currentWave = wave
        this.isWaveActive = false
        this.buildTimeRemaining = buildTime
        this.readyButton.setVisible(true)
        this.updateUI()
        this.updateWaveInfo()
      })

      this.network.on('onBuildPhase', (timeRemaining: number) => {
        this.buildTimeRemaining = timeRemaining
        this.updateTimerDisplay()
      })

      this.network.on('onGoldUpdate', (playerId: string, gold: number, income: number) => {
        if (playerId === this.playerId) {
          this.gold = gold
          this.income = income
        } else {
          const enemy = this.enemies.get(playerId)
          if (enemy) {
            enemy.gold = gold
            enemy.income = income
          }
        }
        this.updateUI()
      })

      this.network.on('onLivesUpdate', (playerId: string, lives: number) => {
        if (playerId === this.playerId) {
          this.lives = lives
        } else {
          const enemy = this.enemies.get(playerId)
          if (enemy) {
            enemy.lives = lives
          }
        }
        this.updateUI()
      })

      this.network.on('onGameOver', (winnerId: string) => {
        const isWinner = winnerId === this.playerId
        this.scene.start('GameOverScene', {
          winner: isWinner ? 'player' : 'enemy',
          wave: this.currentWave,
          stats: this.stats
        })
      })

      this.network.on('onError', (message: string) => {
        console.error('Network error:', message)
      })

    } catch (e) {
      console.error('Failed to setup multiplayer:', e)
      // Fall back to single player
      this.mode = 'single'
      this.startBuildPhase()
    }
  }

  private syncGameState(state: GameState) {
    this.currentWave = state.currentWave
    this.buildTimeRemaining = state.buildTimeRemaining
    this.isWaveActive = state.phase === 'wave'

    // Sync player state
    const myPlayer = state.players[this.playerId]
    if (myPlayer) {
      this.gold = myPlayer.gold
      this.income = myPlayer.income
      this.lives = myPlayer.lives
    }

    // Sync player lane
    const myLane = state.lanes[this.playerId]
    if (myLane) {
      this.playerGrid = myLane.grid
      this.playerTowers = myLane.towers
      this.playerCreeps = myLane.creeps
      this.playerPath = findPath(this.playerGrid, { x: 0, y: 0 }, { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }) || []
    }

    // Sync all enemy lanes
    let enemyIndex = 0
    for (const [playerId, playerState] of Object.entries(state.players)) {
      if (playerId === this.playerId) continue

      let enemy = this.enemies.get(playerId)
      if (!enemy) {
        enemy = this.createEnemyLane(playerId, enemyIndex)
        this.enemies.set(playerId, enemy)
      }

      // Update enemy state
      enemy.gold = playerState.gold
      enemy.income = playerState.income
      enemy.lives = playerState.lives

      // Sync enemy lane
      const enemyLane = state.lanes[playerId]
      if (enemyLane) {
        enemy.grid = enemyLane.grid
        enemy.towers = enemyLane.towers
        enemy.creeps = enemyLane.creeps
        enemy.path = findPath(enemy.grid, { x: 0, y: 0 }, { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }) || []
      }

      enemyIndex++
    }

    this.updateUI()
    this.drawStaticElements()
  }

  private initializeGrids() {
    this.playerGrid = createEmptyGrid(GRID_WIDTH, GRID_HEIGHT)
    this.playerPath = findPath(this.playerGrid, { x: 0, y: 0 }, { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }) || []

    // For single player, create an AI enemy
    if (this.mode === 'single') {
      const aiEnemy = this.createEnemyLane('ai', 0)
      this.enemies.set('ai', aiEnemy)
    }
    // For multiplayer, enemies are populated via syncGameState
  }

  private createGraphicsLayers() {
    // Player lane layers (bottom to top)
    this.playerGridGraphics = this.add.graphics()
    this.playerPathGraphics = this.add.graphics()
    this.playerTowerGraphics = this.add.graphics()
    this.playerCreepGraphics = this.add.graphics()

    // Enemy graphics are created dynamically when enemies are added
    // via createEnemyLane()

    // Shared layers
    this.projectileGraphics = this.add.graphics()
    this.uiOverlayGraphics = this.add.graphics().setDepth(100)
  }

  // Create graphics and lane data for a new enemy (sidebar card style)
  private createEnemyLane(enemyId: string, index: number): EnemyLaneData {
    // Calculate position in sidebar - each card is compact
    const cardHeight = GRID_HEIGHT * this.ENEMY_TILE_SIZE + 50
    const cardY = this.SIDEBAR_Y + index * (cardHeight + 10)
    const color = this.PLAYER_COLORS[(index + 1) % this.PLAYER_COLORS.length]

    const enemy: EnemyLaneData = {
      playerId: enemyId,
      grid: createEmptyGrid(GRID_WIDTH, GRID_HEIGHT),
      towers: [],
      creeps: [],
      path: findPath(createEmptyGrid(GRID_WIDTH, GRID_HEIGHT), { x: 0, y: 0 }, { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }) || [],
      lives: STARTING_LIVES,
      gold: STARTING_GOLD,
      income: BASE_INCOME,
      laneX: this.SIDEBAR_X + 10,
      laneY: cardY + 35,
      tileSize: this.ENEMY_TILE_SIZE,
      graphics: {
        grid: this.add.graphics(),
        path: this.add.graphics(),
        tower: this.add.graphics(),
        creep: this.add.graphics()
      }
    }

    // Card background
    const cardBg = this.add.rectangle(
      this.SIDEBAR_X + this.SIDEBAR_WIDTH / 2,
      cardY + cardHeight / 2,
      this.SIDEBAR_WIDTH - 10,
      cardHeight - 5,
      0x1a1a2e
    )
    cardBg.setStrokeStyle(2, color)

    // Player name with color indicator
    const colorDot = this.add.circle(this.SIDEBAR_X + 18, cardY + 15, 6, color)
    const playerNum = index + 1
    const playerLabel = enemyId === 'ai' ? 'AI OPPONENT' : `PLAYER ${playerNum + 1}`
    this.add.text(this.SIDEBAR_X + 30, cardY + 8, playerLabel, {
      fontSize: '11px',
      fontFamily: 'monospace',
      color: '#ffffff',
      fontStyle: 'bold'
    })

    // Lives text (will be updated dynamically)
    enemy.livesText = this.add.text(this.SIDEBAR_X + 15, cardY + cardHeight - 18, '', {
      fontSize: '12px',
      fontFamily: 'monospace',
      color: '#ff6666'
    })

    return enemy
  }

  private createUI() {
    const width = this.cameras.main.width
    const height = this.cameras.main.height

    // ===== TOP BAR =====
    this.add.rectangle(width / 2, 28, width, 56, 0x0a0a15).setDepth(50)
    this.add.rectangle(width / 2, 28, width - 4, 52, 0x1a1a2e).setDepth(50)

    // Wave info (left)
    this.waveText = this.add.text(20, 8, 'WAVE 1', {
      fontSize: this.isMobile ? '20px' : '26px',
      fontFamily: 'monospace',
      color: '#ffffff',
      fontStyle: 'bold'
    }).setDepth(51)

    // Timer (center-left)
    this.timerText = this.add.text(this.isMobile ? 150 : 200, 8, '', {
      fontSize: this.isMobile ? '18px' : '22px',
      fontFamily: 'monospace',
      color: '#ffcc00',
      fontStyle: 'bold'
    }).setDepth(51)

    // Scoreboard container (right side of top bar)
    this.scoreboardContainer = this.add.container(this.isMobile ? 350 : 500, 8).setDepth(51)
    this.updateScoreboard()

    // ===== PLAYER LANE LABEL =====
    const playerColor = this.PLAYER_COLORS[0]
    this.add.circle(this.PLAYER_LANE_X + 8, this.PLAYER_LANE_Y - 12, 6, playerColor)
    this.add.text(this.PLAYER_LANE_X + 20, this.PLAYER_LANE_Y - 20, 'YOUR LANE', {
      fontSize: '14px',
      fontFamily: 'monospace',
      color: '#4a90d9',
      fontStyle: 'bold'
    })

    // ===== SIDEBAR LABEL =====
    if (!this.isMobile) {
      this.add.text(this.SIDEBAR_X, this.SIDEBAR_Y - 22, 'OPPONENTS', {
        fontSize: '12px',
        fontFamily: 'monospace',
        color: '#888888'
      })
    }

    // ===== BOTTOM PANEL =====
    const panelHeight = height - this.UI_PANEL_Y
    this.add.rectangle(width / 2, this.UI_PANEL_Y + panelHeight / 2, width, panelHeight, 0x0a0a15).setDepth(50)
    this.add.rectangle(width / 2, this.UI_PANEL_Y + panelHeight / 2, width - 4, panelHeight - 4, 0x1a1a2e).setDepth(50)

    // Gold display (larger, more prominent)
    this.goldText = this.add.text(25, this.UI_PANEL_Y + 15, '', {
      fontSize: this.isMobile ? '24px' : '30px',
      fontFamily: 'monospace',
      color: '#ffd700',
      fontStyle: 'bold'
    }).setDepth(51)

    this.incomeText = this.add.text(25, this.UI_PANEL_Y + (this.isMobile ? 45 : 52), '', {
      fontSize: this.isMobile ? '14px' : '16px',
      fontFamily: 'monospace',
      color: '#88ff88'
    }).setDepth(51)

    // Wave preview info
    this.waveInfoText = this.add.text(30, this.UI_PANEL_Y + 85, '', {
      fontSize: '14px',
      fontFamily: 'monospace',
      color: '#aaaaaa'
    }).setDepth(51)

    // Tower buttons
    this.createTowerButtons()

    // Send unit buttons
    this.createSendButtons()

    // Ready button
    this.createReadyButton()

    this.updateUI()
  }

  private createTowerButtons() {
    const buttonWidth = this.isMobile ? 70 : 75
    const buttonHeight = this.isMobile ? 70 : 80
    const buttonSpacing = this.isMobile ? 75 : 85
    const startX = this.isMobile ? 180 : 220
    const towerTypes: TowerType[] = ['arrow', 'cannon', 'frost', 'sniper']

    if (!this.isMobile) {
      this.add.text(startX, this.UI_PANEL_Y + 8, 'TOWERS [1-4]', {
        fontSize: '12px',
        fontFamily: 'monospace',
        color: '#666666'
      }).setDepth(51)
    }

    towerTypes.forEach((type, index) => {
      const x = startX + index * buttonSpacing
      const y = this.UI_PANEL_Y + (this.isMobile ? 50 : 60)
      const def = TOWERS[type]

      const container = this.add.container(x, y).setDepth(51)

      const bg = this.add.rectangle(0, 0, buttonWidth, buttonHeight, 0x252535)
      bg.setStrokeStyle(2, 0x333355)
      bg.setInteractive({ useHandCursor: true })

      // Tower icon with better visuals
      const iconColor = this.getTowerColor(type)
      const icon = this.add.graphics()
      this.drawTowerIcon(icon, 0, this.isMobile ? -12 : -15, type, iconColor)

      // Tower name
      const nameText = this.add.text(0, this.isMobile ? 12 : 15, type.toUpperCase(), {
        fontSize: this.isMobile ? '9px' : '10px',
        fontFamily: 'monospace',
        color: '#ffffff'
      }).setOrigin(0.5)

      // Cost
      const costText = this.add.text(0, this.isMobile ? 26 : 30, `${def.cost}g`, {
        fontSize: this.isMobile ? '12px' : '14px',
        fontFamily: 'monospace',
        color: '#ffd700'
      }).setOrigin(0.5)

      // Hotkey (hide on mobile)
      const hotkeyText = this.add.text(28, -32, `${index + 1}`, {
        fontSize: '10px',
        fontFamily: 'monospace',
        color: '#666666'
      }).setOrigin(0.5).setVisible(!this.isMobile)

      container.add([bg, icon, nameText, costText, hotkeyText])

      bg.on('pointerdown', () => this.selectTower(type))
      bg.on('pointerover', () => {
        this.showTowerTooltip(type, x + 40, y - 50)
        if (this.selectedTowerType !== type) bg.setFillStyle(0x353545)
      })
      bg.on('pointerout', () => {
        this.hideTooltip()
        if (this.selectedTowerType !== type) bg.setFillStyle(0x252535)
      })

      this.towerButtons.set(type, container)
    })
  }

  private createSendButtons() {
    const buttonWidth = this.isMobile ? 70 : 75
    const buttonHeight = this.isMobile ? 70 : 80
    const buttonSpacing = this.isMobile ? 75 : 85
    const startX = this.isMobile ? 500 : 600
    const sendTypes = Object.keys(SEND_UNITS)

    if (!this.isMobile) {
      this.add.text(startX, this.UI_PANEL_Y + 8, 'SEND UNITS [Q-R]', {
        fontSize: '12px',
        fontFamily: 'monospace',
        color: '#666666'
      }).setDepth(51)
    }

    const hotkeys = ['Q', 'W', 'E', 'R']

    sendTypes.forEach((type, index) => {
      const x = startX + index * buttonSpacing
      const y = this.UI_PANEL_Y + (this.isMobile ? 50 : 60)
      const def = SEND_UNITS[type]

      const container = this.add.container(x, y).setDepth(51)

      const bg = this.add.rectangle(0, 0, buttonWidth, buttonHeight, 0x352525)
      bg.setStrokeStyle(2, 0x553333)
      bg.setInteractive({ useHandCursor: true })

      // Unit icon
      const iconY = this.isMobile ? -10 : -15
      const icon = this.add.graphics()
      icon.fillStyle(0xff6b6b)
      if (type === 'swarm') {
        for (let i = 0; i < 3; i++) {
          icon.fillCircle(-8 + i * 8, iconY, 5)
        }
      } else {
        const size = type === 'tank' ? 12 : type === 'soldier' ? 9 : 7
        icon.fillCircle(0, iconY, size)
      }

      // Name
      const nameText = this.add.text(0, this.isMobile ? 5 : 5, type.toUpperCase(), {
        fontSize: this.isMobile ? '8px' : '10px',
        fontFamily: 'monospace',
        color: '#ffffff'
      }).setOrigin(0.5)

      // Cost and income
      const costText = this.add.text(0, this.isMobile ? 17 : 20, `${def.cost}g`, {
        fontSize: this.isMobile ? '11px' : '12px',
        fontFamily: 'monospace',
        color: '#ffd700'
      }).setOrigin(0.5)

      const incText = this.add.text(0, this.isMobile ? 28 : 33, `+${def.incomeBonus}`, {
        fontSize: this.isMobile ? '9px' : '10px',
        fontFamily: 'monospace',
        color: '#88ff88'
      }).setOrigin(0.5)

      // Hotkey (hide on mobile)
      const hotkeyText = this.add.text(28, -32, hotkeys[index], {
        fontSize: '10px',
        fontFamily: 'monospace',
        color: '#666666'
      }).setOrigin(0.5).setVisible(!this.isMobile)

      container.add([bg, icon, nameText, costText, incText, hotkeyText])

      bg.on('pointerdown', () => this.sendUnit(type))
      bg.on('pointerover', () => {
        this.showSendTooltip(type, x + 40, y - 50)
        bg.setFillStyle(0x453535)
      })
      bg.on('pointerout', () => {
        this.hideTooltip()
        bg.setFillStyle(0x352525)
      })

      this.sendButtons.set(type, container)
    })
  }

  private createReadyButton() {
    const x = this.isMobile ? 720 : 1000
    const y = this.UI_PANEL_Y + (this.isMobile ? 50 : 60)
    const buttonWidth = this.isMobile ? 90 : 120
    const buttonHeight = this.isMobile ? 45 : 50

    this.readyButton = this.add.container(x, y).setDepth(51)

    const bg = this.add.rectangle(0, 0, buttonWidth, buttonHeight, 0x2a5a2a)
    bg.setStrokeStyle(2, 0x3a7a3a)
    bg.setInteractive({ useHandCursor: true })

    const text = this.add.text(0, 0, this.isMobile ? 'READY' : 'READY\n[SPACE]', {
      fontSize: this.isMobile ? '14px' : '14px',
      fontFamily: 'monospace',
      color: '#88ff88',
      align: 'center'
    }).setOrigin(0.5)

    this.readyButton.add([bg, text])

    bg.on('pointerdown', () => this.skipBuildPhase())
    bg.on('pointerover', () => bg.setFillStyle(0x3a7a3a))
    bg.on('pointerout', () => bg.setFillStyle(0x2a5a2a))

    // Mobile: Add cancel selection button
    if (this.isMobile) {
      this.createCancelButton()
    }
  }

  private createCancelButton() {
    const x = this.isMobile ? 780 : 920
    const y = this.UI_PANEL_Y + 50

    const container = this.add.container(x, y).setDepth(51)

    const bg = this.add.rectangle(0, 0, 70, 45, 0x5a2a2a)
    bg.setStrokeStyle(2, 0x7a3a3a)
    bg.setInteractive({ useHandCursor: true })

    const text = this.add.text(0, 0, 'X', {
      fontSize: '20px',
      fontFamily: 'monospace',
      color: '#ff8888',
      fontStyle: 'bold'
    }).setOrigin(0.5)

    container.add([bg, text])

    bg.on('pointerdown', () => {
      this.selectedTowerType = null
      this.updateTowerSelection()
    })
    bg.on('pointerover', () => bg.setFillStyle(0x7a3a3a))
    bg.on('pointerout', () => bg.setFillStyle(0x5a2a2a))
  }

  private createTooltip() {
    this.tooltipContainer = this.add.container(0, 0).setDepth(200).setVisible(false)

    this.tooltipBg = this.add.rectangle(0, 0, 200, 100, 0x1a1a2e, 0.95)
    this.tooltipBg.setStrokeStyle(2, 0x4a90d9)

    this.tooltipText = this.add.text(0, 0, '', {
      fontSize: '12px',
      fontFamily: 'monospace',
      color: '#ffffff',
      lineSpacing: 4
    }).setOrigin(0.5)

    this.tooltipContainer.add([this.tooltipBg, this.tooltipText])
  }

  private showTowerTooltip(type: TowerType, x: number, y: number) {
    const def = TOWERS[type]
    const dps = (def.damage * def.attackSpeed).toFixed(1)
    let special = ''
    if (def.special === 'splash') special = `\nSplash: ${def.splashRadius} tile radius`
    else if (def.special === 'slow') special = `\nSlow: ${def.slowPercent}% for ${(def.slowDuration || 0) / 1000}s`

    const text = `${type.toUpperCase()}\n` +
      `Damage: ${def.damage}\n` +
      `Range: ${def.range} tiles\n` +
      `Speed: ${def.attackSpeed}/s\n` +
      `DPS: ${dps}${special}`

    this.tooltipText.setText(text)
    const bounds = this.tooltipText.getBounds()
    this.tooltipBg.setSize(bounds.width + 20, bounds.height + 16)
    this.tooltipContainer.setPosition(x, y)
    this.tooltipContainer.setVisible(true)
  }

  private showSendTooltip(type: string, x: number, y: number) {
    const def = SEND_UNITS[type]
    const text = `${type.toUpperCase()}\n` +
      `HP: ${def.hp}${def.count > 1 ? ' each' : ''}\n` +
      `Speed: ${def.speed}x\n` +
      `Count: ${def.count}\n` +
      `Income: +${def.incomeBonus}/wave`

    this.tooltipText.setText(text)
    const bounds = this.tooltipText.getBounds()
    this.tooltipBg.setSize(bounds.width + 20, bounds.height + 16)
    this.tooltipContainer.setPosition(x, y)
    this.tooltipContainer.setVisible(true)
  }

  private hideTooltip() {
    this.tooltipContainer.setVisible(false)
  }

  private setupInput() {
    // Mouse movement for tower placement preview
    this.input.on('pointermove', (pointer: Phaser.Input.Pointer) => {
      this.updateHover(pointer)
    })

    // Click to place tower
    this.input.on('pointerdown', (pointer: Phaser.Input.Pointer) => {
      if (pointer.y > this.UI_PANEL_Y) return
      if (this.selectedTowerType && this.hoverTile) {
        this.placeTower(this.hoverTile.x, this.hoverTile.y)
      }
    })

    // Keyboard shortcuts
    this.input.keyboard?.on('keydown-ONE', () => this.selectTower('arrow'))
    this.input.keyboard?.on('keydown-TWO', () => this.selectTower('cannon'))
    this.input.keyboard?.on('keydown-THREE', () => this.selectTower('frost'))
    this.input.keyboard?.on('keydown-FOUR', () => this.selectTower('sniper'))

    this.input.keyboard?.on('keydown-Q', () => this.sendUnit('runner'))
    this.input.keyboard?.on('keydown-W', () => this.sendUnit('soldier'))
    this.input.keyboard?.on('keydown-E', () => this.sendUnit('tank'))
    this.input.keyboard?.on('keydown-R', () => this.sendUnit('swarm'))

    this.input.keyboard?.on('keydown-SPACE', () => this.skipBuildPhase())
    this.input.keyboard?.on('keydown-ESC', () => {
      this.selectedTowerType = null
      this.updateTowerSelection()
    })
  }

  private updateHover(pointer: Phaser.Input.Pointer) {
    // Check if hovering over player lane
    const tileX = Math.floor((pointer.x - this.PLAYER_LANE_X) / this.PLAYER_TILE_SIZE)
    const tileY = Math.floor((pointer.y - this.PLAYER_LANE_Y) / this.PLAYER_TILE_SIZE)

    if (tileX >= 0 && tileX < GRID_WIDTH && tileY >= 0 && tileY < GRID_HEIGHT && pointer.y < this.UI_PANEL_Y) {
      this.hoverTile = { x: tileX, y: tileY }

      // Check if hovering over a tower
      this.hoveredTower = this.playerTowers.find(t => t.x === tileX && t.y === tileY) || null
    } else {
      this.hoverTile = null
      this.hoveredTower = null
    }
  }

  private selectTower(type: TowerType) {
    if (this.selectedTowerType === type) {
      this.selectedTowerType = null
    } else {
      this.selectedTowerType = type
    }
    this.updateTowerSelection()
  }

  private updateTowerSelection() {
    this.towerButtons.forEach((container, type) => {
      const bg = container.getAt(0) as Phaser.GameObjects.Rectangle
      if (type === this.selectedTowerType) {
        bg.setFillStyle(0x4a90d9)
        bg.setStrokeStyle(2, 0x6ab0f9)
      } else {
        bg.setFillStyle(0x252535)
        bg.setStrokeStyle(2, 0x333355)
      }
    })
  }

  private drawStaticElements() {
    // Draw player lane
    this.drawGrid(this.playerGridGraphics, this.PLAYER_LANE_X, this.PLAYER_LANE_Y, this.PLAYER_TILE_SIZE, this.playerGrid)
    this.drawPath(this.playerPathGraphics, this.playerPath, this.PLAYER_LANE_X, this.PLAYER_LANE_Y, this.PLAYER_TILE_SIZE)

    // Draw all enemy lanes
    for (const enemy of this.enemies.values()) {
      this.drawGrid(enemy.graphics.grid, enemy.laneX, enemy.laneY, enemy.tileSize, enemy.grid)
      this.drawPath(enemy.graphics.path, enemy.path, enemy.laneX, enemy.laneY, enemy.tileSize)
    }
  }

  private drawGrid(graphics: Phaser.GameObjects.Graphics, offsetX: number, offsetY: number, tileSize: number, grid: boolean[][]) {
    graphics.clear()

    // Seeded random for consistent grass variation
    const seed = (x: number, y: number) => Math.sin(x * 12.9898 + y * 78.233) * 43758.5453 % 1

    // Draw terrain tiles
    for (let y = 0; y < GRID_HEIGHT; y++) {
      for (let x = 0; x < GRID_WIDTH; x++) {
        const px = offsetX + x * tileSize
        const py = offsetY + y * tileSize
        const canBuild = grid[y][x]

        if (canBuild) {
          // Grass terrain with variation
          const variation = Math.abs(seed(x, y))
          const grassBase = 0x2d5a3d // Dark green base
          const r = ((grassBase >> 16) & 0xff) + Math.floor(variation * 20 - 10)
          const g = ((grassBase >> 8) & 0xff) + Math.floor(variation * 30 - 15)
          const b = (grassBase & 0xff) + Math.floor(variation * 15 - 7)
          const grassColor = (Math.max(0, Math.min(255, r)) << 16) |
                            (Math.max(0, Math.min(255, g)) << 8) |
                            Math.max(0, Math.min(255, b))

          graphics.fillStyle(grassColor)
          graphics.fillRect(px, py, tileSize, tileSize)

          // Subtle grass texture (small dots)
          if (tileSize >= 20) {
            graphics.fillStyle(0x3d7a4d, 0.4)
            for (let i = 0; i < 3; i++) {
              const dotX = px + (seed(x + i, y) * 0.8 + 0.1) * tileSize
              const dotY = py + (seed(x, y + i) * 0.8 + 0.1) * tileSize
              graphics.fillCircle(dotX, dotY, tileSize / 15)
            }
          }

          // Subtle grid lines
          graphics.lineStyle(1, 0x1a3a2a, 0.3)
          graphics.strokeRect(px, py, tileSize, tileSize)
        } else {
          // Blocked/tower tile - stone floor
          const stoneBase = 0x3a3a4a
          graphics.fillStyle(stoneBase)
          graphics.fillRect(px, py, tileSize, tileSize)

          // Stone texture
          graphics.fillStyle(0x4a4a5a, 0.5)
          graphics.fillRect(px + 2, py + 2, tileSize - 4, tileSize - 4)
          graphics.lineStyle(1, 0x2a2a3a, 0.5)
          graphics.strokeRect(px + 1, py + 1, tileSize - 2, tileSize - 2)
        }
      }
    }

    // Spawn point - glowing portal
    const spawnX = offsetX
    const spawnY = offsetY
    const spawnCenterX = spawnX + tileSize / 2
    const spawnCenterY = spawnY + tileSize / 2

    // Portal glow
    graphics.fillStyle(0x00ff66, 0.15)
    graphics.fillCircle(spawnCenterX, spawnCenterY, tileSize * 0.7)
    graphics.fillStyle(0x00ff66, 0.25)
    graphics.fillCircle(spawnCenterX, spawnCenterY, tileSize * 0.5)

    // Portal ring
    graphics.lineStyle(3, 0x00ff88, 0.8)
    graphics.strokeCircle(spawnCenterX, spawnCenterY, tileSize * 0.35)
    graphics.lineStyle(2, 0x88ffaa, 0.6)
    graphics.strokeCircle(spawnCenterX, spawnCenterY, tileSize * 0.25)

    // Inner glow
    graphics.fillStyle(0x00ff66, 0.5)
    graphics.fillCircle(spawnCenterX, spawnCenterY, tileSize * 0.15)

    // Exit point - castle/base
    const exitX = offsetX + (GRID_WIDTH - 1) * tileSize
    const exitY = offsetY + (GRID_HEIGHT - 1) * tileSize
    const exitCenterX = exitX + tileSize / 2
    const exitCenterY = exitY + tileSize / 2

    // Base platform
    graphics.fillStyle(0x8b4513)
    graphics.fillRect(exitX + 2, exitY + 2, tileSize - 4, tileSize - 4)

    // Castle walls
    graphics.fillStyle(0x696969)
    graphics.fillRect(exitX + 4, exitY + tileSize * 0.3, tileSize - 8, tileSize * 0.5)

    // Battlements
    graphics.fillStyle(0x808080)
    const battWidth = tileSize / 5
    for (let i = 0; i < 3; i++) {
      graphics.fillRect(exitX + 6 + i * battWidth * 1.3, exitY + tileSize * 0.2, battWidth * 0.8, tileSize * 0.15)
    }

    // Door
    graphics.fillStyle(0x4a3728)
    graphics.fillRect(exitCenterX - tileSize * 0.12, exitY + tileSize * 0.5, tileSize * 0.24, tileSize * 0.35)

    // Warning glow
    graphics.lineStyle(2, 0xff6600, 0.6)
    graphics.strokeRect(exitX + 1, exitY + 1, tileSize - 2, tileSize - 2)
  }

  private drawPath(graphics: Phaser.GameObjects.Graphics, path: Point[], offsetX: number, offsetY: number, tileSize: number) {
    graphics.clear()
    if (path.length < 2) return

    const pathWidth = tileSize * 0.6

    // Draw dirt road under the path
    for (let i = 0; i < path.length; i++) {
      const p = path[i]
      const px = offsetX + p.x * tileSize + tileSize / 2
      const py = offsetY + p.y * tileSize + tileSize / 2

      // Dirt base
      graphics.fillStyle(0x8b7355, 0.7)
      graphics.fillCircle(px, py, pathWidth * 0.6)

      // Connect to next point
      if (i < path.length - 1) {
        const next = path[i + 1]
        const nx = offsetX + next.x * tileSize + tileSize / 2
        const ny = offsetY + next.y * tileSize + tileSize / 2

        // Draw road segment
        graphics.lineStyle(pathWidth, 0x8b7355, 0.6)
        graphics.lineBetween(px, py, nx, ny)

        // Road edges (darker)
        graphics.lineStyle(pathWidth + 4, 0x5a4a3a, 0.3)
        graphics.lineBetween(px, py, nx, ny)
      }
    }

    // Draw subtle direction indicators (arrows along path)
    if (tileSize >= 20) {
      graphics.fillStyle(0xd4c4a8, 0.4)
      for (let i = 2; i < path.length - 2; i += 3) {
        const p = path[i]
        const next = path[Math.min(i + 1, path.length - 1)]
        const px = offsetX + p.x * tileSize + tileSize / 2
        const py = offsetY + p.y * tileSize + tileSize / 2

        // Direction arrow
        const dx = next.x - p.x
        const dy = next.y - p.y
        const angle = Math.atan2(dy, dx)
        const arrowSize = tileSize * 0.15

        graphics.fillTriangle(
          px + Math.cos(angle) * arrowSize * 2,
          py + Math.sin(angle) * arrowSize * 2,
          px + Math.cos(angle + 2.5) * arrowSize,
          py + Math.sin(angle + 2.5) * arrowSize,
          px + Math.cos(angle - 2.5) * arrowSize,
          py + Math.sin(angle - 2.5) * arrowSize
        )
      }
    }
  }

  private drawTowers(graphics: Phaser.GameObjects.Graphics, towers: Tower[], offsetX: number, offsetY: number, tileSize: number) {
    graphics.clear()

    for (const tower of towers) {
      const px = offsetX + tower.x * tileSize
      const py = offsetY + tower.y * tileSize
      const cx = px + tileSize / 2
      const cy = py + tileSize / 2
      const s = tileSize / 40 // Scale factor

      // Show range circle for hovered tower
      if (this.hoveredTower === tower) {
        const def = TOWERS[tower.type]
        graphics.fillStyle(this.getTowerColor(tower.type), 0.1)
        graphics.fillCircle(cx, cy, def.range * tileSize)
        graphics.lineStyle(2, this.getTowerColor(tower.type), 0.4)
        graphics.strokeCircle(cx, cy, def.range * tileSize)
      }

      // Draw tower based on type
      switch (tower.type) {
        case 'arrow':
          this.drawArrowTower(graphics, cx, cy, s)
          break
        case 'cannon':
          this.drawCannonTower(graphics, cx, cy, s)
          break
        case 'frost':
          this.drawFrostTower(graphics, cx, cy, s)
          break
        case 'sniper':
          this.drawSniperTower(graphics, cx, cy, s)
          break
      }
    }
  }

  private drawArrowTower(graphics: Phaser.GameObjects.Graphics, cx: number, cy: number, s: number) {
    // Stone base
    graphics.fillStyle(0x5a5a6a)
    graphics.fillRoundedRect(cx - 14 * s, cy - 14 * s, 28 * s, 28 * s, 4 * s)

    // Wooden platform
    graphics.fillStyle(0x8b6914)
    graphics.fillRoundedRect(cx - 12 * s, cy - 12 * s, 24 * s, 24 * s, 3 * s)

    // Tower body (wooden)
    graphics.fillStyle(0xa07828)
    graphics.fillRect(cx - 8 * s, cy - 10 * s, 16 * s, 18 * s)

    // Archer figure
    graphics.fillStyle(0x4a90d9)
    graphics.fillCircle(cx, cy - 4 * s, 5 * s) // Head
    graphics.fillRect(cx - 4 * s, cy + 1 * s, 8 * s, 8 * s) // Body

    // Bow
    graphics.lineStyle(2 * s, 0x654321)
    graphics.beginPath()
    graphics.arc(cx + 6 * s, cy, 8 * s, -1.2, 1.2)
    graphics.strokePath()

    // Arrow
    graphics.lineStyle(1.5 * s, 0x888888)
    graphics.lineBetween(cx - 2 * s, cy, cx + 12 * s, cy)
    graphics.fillStyle(0xcccccc)
    graphics.fillTriangle(cx + 12 * s, cy - 2 * s, cx + 12 * s, cy + 2 * s, cx + 16 * s, cy)
  }

  private drawCannonTower(graphics: Phaser.GameObjects.Graphics, cx: number, cy: number, s: number) {
    // Stone base (fortress-like)
    graphics.fillStyle(0x4a4a5a)
    graphics.fillRoundedRect(cx - 16 * s, cy - 16 * s, 32 * s, 32 * s, 3 * s)

    // Red brick pattern
    graphics.fillStyle(0x8b3a3a)
    graphics.fillRect(cx - 14 * s, cy - 14 * s, 28 * s, 28 * s)

    // Brick lines
    graphics.lineStyle(1 * s, 0x6a2a2a)
    for (let i = -2; i <= 2; i++) {
      graphics.lineBetween(cx - 14 * s, cy + i * 5 * s, cx + 14 * s, cy + i * 5 * s)
    }

    // Cannon mount (circular)
    graphics.fillStyle(0x3a3a4a)
    graphics.fillCircle(cx, cy, 10 * s)

    // Cannon barrel
    graphics.fillStyle(0x2a2a3a)
    graphics.fillRoundedRect(cx - 4 * s, cy - 16 * s, 8 * s, 18 * s, 2 * s)

    // Barrel opening
    graphics.fillStyle(0x1a1a2a)
    graphics.fillCircle(cx, cy - 14 * s, 3 * s)

    // Metal rings on barrel
    graphics.fillStyle(0x5a5a6a)
    graphics.fillRect(cx - 5 * s, cy - 8 * s, 10 * s, 2 * s)
    graphics.fillRect(cx - 5 * s, cy - 2 * s, 10 * s, 2 * s)

    // Cannonball stack
    graphics.fillStyle(0x1a1a1a)
    graphics.fillCircle(cx - 6 * s, cy + 8 * s, 3 * s)
    graphics.fillCircle(cx + 6 * s, cy + 8 * s, 3 * s)
    graphics.fillCircle(cx, cy + 6 * s, 3 * s)
  }

  private drawFrostTower(graphics: Phaser.GameObjects.Graphics, cx: number, cy: number, s: number) {
    // Ice platform
    graphics.fillStyle(0x1a4a6a, 0.8)
    graphics.fillRoundedRect(cx - 14 * s, cy - 14 * s, 28 * s, 28 * s, 4 * s)

    // Outer glow
    graphics.fillStyle(0x4ad9d9, 0.2)
    graphics.fillCircle(cx, cy, 16 * s)

    // Crystal spire (main)
    graphics.fillStyle(0x6aeaea)
    graphics.fillTriangle(cx, cy - 18 * s, cx - 8 * s, cy + 10 * s, cx + 8 * s, cy + 10 * s)

    // Crystal highlight
    graphics.fillStyle(0xaaffff, 0.6)
    graphics.fillTriangle(cx - 2 * s, cy - 14 * s, cx - 6 * s, cy + 6 * s, cx + 2 * s, cy + 6 * s)

    // Side crystals
    graphics.fillStyle(0x4acaca)
    graphics.fillTriangle(cx - 10 * s, cy - 6 * s, cx - 14 * s, cy + 8 * s, cx - 4 * s, cy + 8 * s)
    graphics.fillTriangle(cx + 10 * s, cy - 6 * s, cx + 4 * s, cy + 8 * s, cx + 14 * s, cy + 8 * s)

    // Ice particles
    graphics.fillStyle(0xffffff, 0.8)
    graphics.fillCircle(cx - 4 * s, cy - 8 * s, 2 * s)
    graphics.fillCircle(cx + 5 * s, cy - 4 * s, 1.5 * s)
    graphics.fillCircle(cx - 2 * s, cy + 4 * s, 1.5 * s)
  }

  private drawSniperTower(graphics: Phaser.GameObjects.Graphics, cx: number, cy: number, s: number) {
    // Concrete base
    graphics.fillStyle(0x5a5a5a)
    graphics.fillRoundedRect(cx - 14 * s, cy - 14 * s, 28 * s, 28 * s, 2 * s)

    // Platform
    graphics.fillStyle(0x4a4a4a)
    graphics.fillRect(cx - 12 * s, cy - 12 * s, 24 * s, 24 * s)

    // Tall tower body
    graphics.fillStyle(0x6a6a6a)
    graphics.fillRect(cx - 6 * s, cy - 16 * s, 12 * s, 26 * s)

    // Observation deck
    graphics.fillStyle(0x7a7a7a)
    graphics.fillRect(cx - 10 * s, cy - 14 * s, 20 * s, 6 * s)

    // Scope/rifle
    graphics.fillStyle(0xd9d94a)
    graphics.fillRect(cx - 2 * s, cy - 20 * s, 4 * s, 8 * s)

    // Scope lens
    graphics.fillStyle(0x2a2a8a)
    graphics.fillCircle(cx, cy - 20 * s, 3 * s)
    graphics.fillStyle(0xaaaaff, 0.5)
    graphics.fillCircle(cx - 1 * s, cy - 21 * s, 1.5 * s)

    // Sniper figure
    graphics.fillStyle(0x3a5a3a)
    graphics.fillCircle(cx, cy - 8 * s, 4 * s) // Head (helmeted)

    // Rifle barrel pointing out
    graphics.fillStyle(0x4a4a4a)
    graphics.fillRect(cx + 4 * s, cy - 9 * s, 12 * s, 2 * s)
  }

  private drawTowerIcon(graphics: Phaser.GameObjects.Graphics, x: number, y: number, type: TowerType, color: number) {
    graphics.fillStyle(color)

    switch (type) {
      case 'arrow':
        // Triangle/arrow shape
        graphics.fillTriangle(x, y - 12, x - 10, y + 8, x + 10, y + 8)
        break
      case 'cannon':
        // Circle with smaller circle
        graphics.fillCircle(x, y, 12)
        graphics.fillStyle(0x222222)
        graphics.fillCircle(x, y, 6)
        break
      case 'frost':
        // Hexagon-ish
        graphics.fillCircle(x, y, 10)
        graphics.fillStyle(0xffffff, 0.5)
        graphics.fillCircle(x - 3, y - 3, 4)
        break
      case 'sniper':
        // Cross-hair
        graphics.fillRect(x - 2, y - 12, 4, 24)
        graphics.fillRect(x - 12, y - 2, 24, 4)
        graphics.fillStyle(0x222222)
        graphics.fillCircle(x, y, 4)
        break
    }
  }

  private getTowerColor(type: TowerType): number {
    const colors: Record<TowerType, number> = {
      arrow: 0x4a90d9,
      cannon: 0xd94a4a,
      frost: 0x4ad9d9,
      sniper: 0xd9d94a
    }
    return colors[type]
  }

  private placeTower(x: number, y: number) {
    if (!this.selectedTowerType) return
    if (!this.canPlaceTowerAt(x, y)) return

    const def = TOWERS[this.selectedTowerType]

    // In multiplayer, send to server
    if (this.mode === 'multi' && this.network) {
      this.network.placeTower(this.selectedTowerType, x, y)
      this.stats.towersBuilt++
      // Floating text
      this.createFloatingText(
        this.PLAYER_LANE_X + x * this.PLAYER_TILE_SIZE + this.PLAYER_TILE_SIZE / 2,
        this.PLAYER_LANE_Y + y * this.PLAYER_TILE_SIZE,
        `-${def.cost}g`,
        '#ff6666'
      )
      return
    }

    // Single player mode
    const tower: Tower = {
      id: `tower_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      type: this.selectedTowerType,
      x,
      y,
      playerId: 'player',
      lastAttackTime: 0
    }

    this.playerTowers.push(tower)
    this.playerGrid[y][x] = false
    this.gold -= def.cost
    this.stats.towersBuilt++

    // Recalculate path
    this.playerPath = findPath(this.playerGrid, { x: 0, y: 0 }, { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }) || []

    // Update existing creep paths
    this.playerCreeps = this.playerCreeps.filter(creep => {
      const newPath = findPath(
        this.playerGrid,
        { x: Math.floor(creep.x), y: Math.floor(creep.y) },
        { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }
      )
      if (newPath && newPath.length > 0) {
        creep.path = newPath
        creep.pathIndex = 0
        return true
      }
      // Creep is trapped - destroy it
      return false
    })

    this.updateUI()
    this.drawGrid(this.playerGridGraphics, this.PLAYER_LANE_X, this.PLAYER_LANE_Y, this.PLAYER_TILE_SIZE, this.playerGrid)
    this.drawPath(this.playerPathGraphics, this.playerPath, this.PLAYER_LANE_X, this.PLAYER_LANE_Y, this.PLAYER_TILE_SIZE)

    // Floating text
    this.createFloatingText(
      this.PLAYER_LANE_X + x * this.PLAYER_TILE_SIZE + this.PLAYER_TILE_SIZE / 2,
      this.PLAYER_LANE_Y + y * this.PLAYER_TILE_SIZE,
      `-${def.cost}g`,
      '#ff6666'
    )
  }

  private canPlaceTowerAt(x: number, y: number): boolean {
    if (!this.selectedTowerType) return false
    if (!this.playerGrid[y]?.[x]) return false

    const def = TOWERS[this.selectedTowerType]
    if (this.gold < def.cost) return false

    return canPlaceTower(this.playerGrid, { x: 0, y: 0 }, { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }, { x, y })
  }

  private sendUnit(type: string) {
    const def = SEND_UNITS[type]
    if (!def || this.gold < def.cost) return

    // In multiplayer, send to server
    if (this.mode === 'multi' && this.network) {
      this.network.sendUnit(type as SendUnitType)
      this.stats.unitsSent += def.count
      this.createFloatingText(600, this.UI_PANEL_Y + 30, `-${def.cost}g`, '#ff6666')
      this.createFloatingText(650, this.UI_PANEL_Y + 30, `+${def.incomeBonus}`, '#88ff88')
      return
    }

    // Single player mode
    this.gold -= def.cost
    this.income += def.incomeBonus
    this.stats.unitsSent += def.count

    // Spawn units in all enemy lanes
    for (const enemy of this.enemies.values()) {
      for (let i = 0; i < def.count; i++) {
        this.time.delayedCall(i * 150, () => {
          this.spawnCreepInLane(enemy, def.hp, def.speed, 0, true)
        })
      }
    }

    this.updateUI()
    this.createFloatingText(600, this.UI_PANEL_Y + 30, `-${def.cost}g`, '#ff6666')
    this.createFloatingText(650, this.UI_PANEL_Y + 30, `+${def.incomeBonus}`, '#88ff88')
  }

  private spawnCreep(creepArray: Creep[], path: Point[], hp: number, speed: number, bounty: number, isSent: boolean) {
    if (path.length === 0) return

    const creep: Creep = {
      id: `creep_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      hp,
      maxHp: hp,
      speed,
      bounty,
      x: path[0].x,
      y: path[0].y,
      pathIndex: 0,
      path: [...path],
      laneId: creepArray === this.playerCreeps ? 'player' : 'enemy',
      isSent,
      slowedUntil: 0
    }

    creepArray.push(creep)
  }

  private startBuildPhase() {
    this.currentWave++
    this.isWaveActive = false
    this.buildTimeRemaining = BUILD_TIME_SECONDS

    // Award income (not on first wave)
    if (this.currentWave > 1) {
      this.gold += this.income
      // Award income to all enemies
      for (const enemy of this.enemies.values()) {
        enemy.gold += enemy.income
      }
      this.createFloatingText(80, this.UI_PANEL_Y + 10, `+${this.income}g`, '#88ff88')
    }

    this.readyButton.setVisible(true)
    this.updateUI()
    this.updateWaveInfo()

    this.buildTimer = this.time.addEvent({
      delay: 1000,
      callback: () => {
        this.buildTimeRemaining--
        this.updateTimerDisplay()
        if (this.buildTimeRemaining <= 0) {
          this.startWave()
        }
      },
      repeat: BUILD_TIME_SECONDS - 1
    })
  }

  private skipBuildPhase() {
    if (this.isWaveActive || this.buildTimeRemaining <= 0) return

    // In multiplayer, send ready signal
    if (this.mode === 'multi' && this.network) {
      this.network.ready()
      this.createFloatingText(640, 300, 'READY!', '#00ff88')
      return
    }

    // Single player mode
    this.buildTimer?.destroy()
    this.startWave()
  }

  private startWave() {
    this.isWaveActive = true
    this.readyButton.setVisible(false)
    this.updateTimerDisplay()

    const config = getWaveConfig(this.currentWave)
    this.creepsToSpawn = config.count

    // Initialize enemy creeps to spawn for each enemy
    for (const enemyId of this.enemies.keys()) {
      this.enemyCreepsToSpawn.set(enemyId, config.count)
    }

    // Spawn wave creeps - track timer for cleanup
    const spawnTimer = this.time.addEvent({
      delay: 400,
      callback: () => {
        // Spawn creeps in player lane
        if (this.creepsToSpawn > 0) {
          this.spawnCreep(this.playerCreeps, this.playerPath, config.hp, config.speed, config.bounty, false)
          this.creepsToSpawn--
        }

        // Spawn creeps in all enemy lanes
        for (const [enemyId, enemy] of this.enemies) {
          const remaining = this.enemyCreepsToSpawn.get(enemyId) || 0
          if (remaining > 0) {
            this.spawnCreepInLane(enemy, config.hp, config.speed, config.bounty, false)
            this.enemyCreepsToSpawn.set(enemyId, remaining - 1)
          }
        }
      },
      repeat: config.count - 1
    })
    this.activeTimers.push(spawnTimer)

    // AI actions
    if (this.mode === 'single') {
      this.aiTakeTurn()
    }
  }

  // Helper to spawn creep in a specific enemy lane
  private spawnCreepInLane(enemy: EnemyLaneData, hp: number, speed: number, bounty: number, isSent: boolean) {
    if (enemy.path.length === 0) return

    const creep: Creep = {
      id: `creep_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      hp,
      maxHp: hp,
      speed,
      bounty,
      x: enemy.path[0].x,
      y: enemy.path[0].y,
      pathIndex: 0,
      path: [...enemy.path],
      laneId: enemy.playerId,
      isSent,
      slowedUntil: 0
    }

    enemy.creeps.push(creep)
  }

  private aiTakeTurn() {
    const aiEnemy = this.enemies.get('ai')
    if (!aiEnemy) return

    // Smarter AI: builds towards creating a maze
    const towersToPlace = Math.min(3, Math.floor(aiEnemy.gold / 50))

    for (let t = 0; t < towersToPlace; t++) {
      this.time.delayedCall(t * 300, () => {
        this.aiPlaceTower()
      })
    }

    // AI also sends units occasionally
    if (this.currentWave > 2 && Math.random() < 0.4) {
      const sendTypes = Object.keys(SEND_UNITS)
      const type = sendTypes[Math.floor(Math.random() * sendTypes.length)]
      const def = SEND_UNITS[type]
      if (aiEnemy.gold >= def.cost) {
        aiEnemy.gold -= def.cost
        aiEnemy.income += def.incomeBonus
        for (let i = 0; i < def.count; i++) {
          this.time.delayedCall(i * 150, () => {
            this.spawnCreep(this.playerCreeps, this.playerPath, def.hp, def.speed, 0, true)
          })
        }
      }
    }
  }

  private aiPlaceTower() {
    const aiEnemy = this.enemies.get('ai')
    if (!aiEnemy) return

    // Strategic AI: tries to extend the path by placing towers near the current path
    const affordableTowers = Object.entries(TOWERS).filter(([_, def]) => aiEnemy.gold >= def.cost)
    if (affordableTowers.length === 0) return

    // Prefer cheaper towers for maze building
    const [type, def] = affordableTowers[0]

    // Find positions adjacent to the path that would extend it
    let bestPos: Point | null = null
    let bestPathLength = aiEnemy.path.length

    // Try positions near the middle of the path
    const pathMid = Math.floor(aiEnemy.path.length / 2)
    const searchRange = 5

    for (let i = Math.max(1, pathMid - searchRange); i < Math.min(aiEnemy.path.length - 1, pathMid + searchRange); i++) {
      const pathPoint = aiEnemy.path[i]

      // Check adjacent cells
      const adjacents = [
        { x: pathPoint.x - 1, y: pathPoint.y },
        { x: pathPoint.x + 1, y: pathPoint.y },
        { x: pathPoint.x, y: pathPoint.y - 1 },
        { x: pathPoint.x, y: pathPoint.y + 1 }
      ]

      for (const adj of adjacents) {
        if (adj.x < 0 || adj.x >= GRID_WIDTH || adj.y < 0 || adj.y >= GRID_HEIGHT) continue
        if (!aiEnemy.grid[adj.y][adj.x]) continue

        // Check if this placement is valid and extends path
        if (canPlaceTower(aiEnemy.grid, { x: 0, y: 0 }, { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }, adj)) {
          // Simulate placement
          const testGrid = aiEnemy.grid.map(row => [...row])
          testGrid[adj.y][adj.x] = false
          const newPath = findPath(testGrid, { x: 0, y: 0 }, { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 })

          if (newPath && newPath.length > bestPathLength) {
            bestPathLength = newPath.length
            bestPos = adj
          }
        }
      }
    }

    // If no path-extending position found, try random valid position
    if (!bestPos) {
      for (let attempts = 0; attempts < 30; attempts++) {
        const x = Math.floor(Math.random() * GRID_WIDTH)
        const y = Math.floor(Math.random() * GRID_HEIGHT)

        if (aiEnemy.grid[y][x] && canPlaceTower(aiEnemy.grid, { x: 0, y: 0 }, { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }, { x, y })) {
          bestPos = { x, y }
          break
        }
      }
    }

    if (bestPos) {
      const tower: Tower = {
        id: `tower_ai_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        type: type as TowerType,
        x: bestPos.x,
        y: bestPos.y,
        playerId: 'ai',
        lastAttackTime: 0
      }

      aiEnemy.towers.push(tower)
      aiEnemy.grid[bestPos.y][bestPos.x] = false
      aiEnemy.gold -= def.cost

      aiEnemy.path = findPath(aiEnemy.grid, { x: 0, y: 0 }, { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }) || []

      // Update enemy creep paths
      aiEnemy.creeps = aiEnemy.creeps.filter(creep => {
        const newPath = findPath(
          aiEnemy.grid,
          { x: Math.floor(creep.x), y: Math.floor(creep.y) },
          { x: GRID_WIDTH - 1, y: GRID_HEIGHT - 1 }
        )
        if (newPath && newPath.length > 0) {
          creep.path = newPath
          creep.pathIndex = 0
          return true
        }
        // Creep is trapped - destroy it
        return false
      })

      // Redraw enemy lane
      this.drawGrid(aiEnemy.graphics.grid, aiEnemy.laneX, aiEnemy.laneY, aiEnemy.tileSize, aiEnemy.grid)
      this.drawPath(aiEnemy.graphics.path, aiEnemy.path, aiEnemy.laneX, aiEnemy.laneY, aiEnemy.tileSize)
    }
  }

  private createFloatingText(x: number, y: number, text: string, color: string) {
    const floatText = this.add.text(x, y, text, {
      fontSize: '16px',
      fontFamily: 'monospace',
      color: color,
      fontStyle: 'bold'
    }).setOrigin(0.5).setDepth(150)

    this.floatingTexts.push({
      text: floatText,
      vy: -1.5,
      life: 60
    })
  }

  private updateUI() {
    this.goldText.setText(`${this.gold} GOLD`)
    this.incomeText.setText(`+${this.income} per wave`)
    this.waveText.setText(`WAVE ${this.currentWave}`)

    // Update scoreboard (player lives in top bar)
    this.updateScoreboard()

    // Update enemy lives in sidebar cards
    for (const enemy of this.enemies.values()) {
      if (enemy.livesText) {
        enemy.livesText.setText(`♥ ${enemy.lives}`)
      }
    }

    // Update tower button affordability
    this.towerButtons.forEach((container, type) => {
      const def = TOWERS[type]
      const costText = container.getAt(3) as Phaser.GameObjects.Text
      costText.setColor(this.gold >= def.cost ? '#ffd700' : '#ff4444')
    })

    // Update send button affordability
    this.sendButtons.forEach((container, type) => {
      const def = SEND_UNITS[type]
      const costText = container.getAt(3) as Phaser.GameObjects.Text
      costText.setColor(this.gold >= def.cost ? '#ffd700' : '#ff4444')
    })
  }

  private updateScoreboard() {
    this.scoreboardContainer.removeAll(true)

    // Player entry (first)
    const playerColor = this.PLAYER_COLORS[0]
    const playerDot = this.add.circle(0, 10, 5, playerColor)
    const playerLabel = this.add.text(12, 3, 'YOU', {
      fontSize: '12px',
      fontFamily: 'monospace',
      color: '#ffffff',
      fontStyle: 'bold'
    })
    const playerLives = this.add.text(50, 3, `♥${this.lives}`, {
      fontSize: '12px',
      fontFamily: 'monospace',
      color: this.lives > 5 ? '#88ff88' : this.lives > 2 ? '#ffcc00' : '#ff4444'
    })
    this.scoreboardContainer.add([playerDot, playerLabel, playerLives])

    // Enemy entries
    let offsetX = 100
    let index = 0
    for (const enemy of this.enemies.values()) {
      const color = this.PLAYER_COLORS[(index + 1) % this.PLAYER_COLORS.length]
      const dot = this.add.circle(offsetX, 10, 5, color)
      const label = enemy.playerId === 'ai' ? 'AI' : `P${index + 2}`
      const labelText = this.add.text(offsetX + 12, 3, label, {
        fontSize: '12px',
        fontFamily: 'monospace',
        color: '#aaaaaa'
      })
      const livesText = this.add.text(offsetX + 38, 3, `♥${enemy.lives}`, {
        fontSize: '12px',
        fontFamily: 'monospace',
        color: enemy.lives > 5 ? '#88ff88' : enemy.lives > 2 ? '#ffcc00' : '#ff4444'
      })
      this.scoreboardContainer.add([dot, labelText, livesText])
      offsetX += 80
      index++
    }
  }

  private updateTimerDisplay() {
    if (this.isWaveActive) {
      this.timerText.setText('⚔ WAVE IN PROGRESS')
      this.timerText.setColor('#ff6666')
    } else if (this.buildTimeRemaining > 0) {
      this.timerText.setText(`BUILD TIME: ${this.buildTimeRemaining}s`)
      this.timerText.setColor(this.buildTimeRemaining <= 5 ? '#ff6666' : '#ffcc00')
    }
  }

  private updateWaveInfo() {
    const nextConfig = getWaveConfig(this.currentWave)
    const isBoss = nextConfig.isBoss
    this.waveInfoText.setText(
      `Next: ${nextConfig.count} creeps | ${nextConfig.hp} HP | ${nextConfig.bounty}g each${isBoss ? ' [BOSS]' : ''}`
    )
  }

  update(time: number, delta: number) {
    this.updateCreeps(delta)
    this.updateTowerAttacks(time)
    this.updateProjectiles(delta)
    this.updateFloatingTexts()
    this.drawDynamicElements()
    this.checkWaveEnd()
    this.checkGameOver()
  }

  private updateCreeps(delta: number) {
    const moveCreeps = (creeps: Creep[]) => {
      for (const creep of creeps) {
        if (creep.pathIndex >= creep.path.length - 1) continue

        const target = creep.path[creep.pathIndex + 1]
        const dx = target.x - creep.x
        const dy = target.y - creep.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        // Skip if already at target (avoid division by zero)
        if (dist < 0.001) {
          creep.x = target.x
          creep.y = target.y
          creep.pathIndex++
          continue
        }

        let speed = creep.speed
        if (creep.slowedUntil > Date.now()) {
          speed *= 0.7
        }

        const moveAmount = speed * (delta / 1000) * 2

        if (dist < moveAmount) {
          creep.x = target.x
          creep.y = target.y
          creep.pathIndex++
        } else {
          creep.x += (dx / dist) * moveAmount
          creep.y += (dy / dist) * moveAmount
        }
      }
    }

    // Move player creeps
    moveCreeps(this.playerCreeps)

    // Move all enemy creeps
    for (const enemy of this.enemies.values()) {
      moveCreeps(enemy.creeps)
    }

    // Check escaped creeps - player lane
    this.playerCreeps = this.playerCreeps.filter(creep => {
      if (creep.pathIndex >= creep.path.length - 1 &&
          Math.abs(creep.x - (GRID_WIDTH - 1)) < 0.2 &&
          Math.abs(creep.y - (GRID_HEIGHT - 1)) < 0.2) {
        this.lives--
        this.updateUI()
        return false
      }
      return true
    })

    // Check escaped creeps - all enemy lanes
    for (const enemy of this.enemies.values()) {
      enemy.creeps = enemy.creeps.filter(creep => {
        if (creep.pathIndex >= creep.path.length - 1 &&
            Math.abs(creep.x - (GRID_WIDTH - 1)) < 0.2 &&
            Math.abs(creep.y - (GRID_HEIGHT - 1)) < 0.2) {
          enemy.lives--
          this.updateUI()
          return false
        }
        return true
      })
    }
  }

  private updateTowerAttacks(time: number) {
    // Player towers attack creeps in player lane
    this.processAttacks(this.playerTowers, this.playerCreeps, this.PLAYER_LANE_X, this.PLAYER_LANE_Y, this.PLAYER_TILE_SIZE, true, time)

    // All enemy towers attack creeps in their respective lanes
    for (const enemy of this.enemies.values()) {
      this.processAttacks(enemy.towers, enemy.creeps, enemy.laneX, enemy.laneY, enemy.tileSize, false, time)
    }
  }

  private processAttacks(towers: Tower[], creeps: Creep[], laneX: number, laneY: number, tileSize: number, isPlayer: boolean, time: number) {
    for (const tower of towers) {
      const def = TOWERS[tower.type]
      const attackInterval = 1000 / def.attackSpeed

      if (time - tower.lastAttackTime < attackInterval) continue

      // Find target
      let target: Creep | null = null
      let maxProgress = -1

      for (const creep of creeps) {
        const dx = creep.x - tower.x
        const dy = creep.y - tower.y
        const dist = Math.sqrt(dx * dx + dy * dy)

        if (dist <= def.range && creep.pathIndex > maxProgress) {
          maxProgress = creep.pathIndex
          target = creep
        }
      }

      if (target) {
        tower.lastAttackTime = time

        // Create projectile
        this.projectiles.push({
          id: `proj_${Date.now()}`,
          fromX: laneX + tower.x * tileSize + tileSize / 2,
          fromY: laneY + tower.y * tileSize + tileSize / 2,
          toX: laneX + target.x * tileSize + tileSize / 2,
          toY: laneY + target.y * tileSize + tileSize / 2,
          towerType: tower.type,
          createdAt: time
        })

        // Deal damage
        target.hp -= def.damage
        this.stats.damageDealt += def.damage

        // Floating damage number
        if (isPlayer) {
          this.createFloatingText(
            laneX + target.x * tileSize + tileSize / 2,
            laneY + target.y * tileSize,
            `-${def.damage}`,
            '#ffffff'
          )
        }

        // Apply effects
        if (def.special === 'slow' && def.slowDuration) {
          target.slowedUntil = Date.now() + def.slowDuration
        }

        if (def.special === 'splash' && def.splashRadius) {
          for (const other of creeps) {
            if (other === target) continue
            const odx = other.x - target.x
            const ody = other.y - target.y
            const odist = Math.sqrt(odx * odx + ody * ody)
            if (odist <= def.splashRadius) {
              other.hp -= Math.floor(def.damage * 0.5)
            }
          }
        }

        // Kill check
        if (target.hp <= 0) {
          const idx = creeps.indexOf(target)
          if (idx > -1) {
            if (isPlayer && target.bounty > 0) {
              this.gold += target.bounty
              this.stats.goldEarned += target.bounty
              this.stats.creepsKilled++
              this.createFloatingText(
                laneX + target.x * tileSize + tileSize / 2,
                laneY + target.y * tileSize - 10,
                `+${target.bounty}g`,
                '#ffd700'
              )
            }
            creeps.splice(idx, 1)
            this.updateUI()
          }
        }
      }
    }
  }

  private updateProjectiles(delta: number) {
    const now = Date.now()
    this.projectiles = this.projectiles.filter(p => now - p.createdAt < 120)
  }

  private updateFloatingTexts() {
    this.floatingTexts = this.floatingTexts.filter(ft => {
      ft.text.y += ft.vy
      ft.text.setAlpha(ft.life / 60)
      ft.life--
      if (ft.life <= 0) {
        ft.text.destroy()
        return false
      }
      return true
    })
  }

  private drawDynamicElements() {
    // Draw player towers and creeps
    this.drawTowers(this.playerTowerGraphics, this.playerTowers, this.PLAYER_LANE_X, this.PLAYER_LANE_Y, this.PLAYER_TILE_SIZE)
    this.drawCreeps(this.playerCreepGraphics, this.playerCreeps, this.PLAYER_LANE_X, this.PLAYER_LANE_Y, this.PLAYER_TILE_SIZE)

    // Draw all enemy lanes
    for (const enemy of this.enemies.values()) {
      this.drawTowers(enemy.graphics.tower, enemy.towers, enemy.laneX, enemy.laneY, enemy.tileSize)
      this.drawCreeps(enemy.graphics.creep, enemy.creeps, enemy.laneX, enemy.laneY, enemy.tileSize)
    }

    // Draw projectiles
    this.drawProjectiles()

    // Draw placement preview
    this.drawPlacementPreview()
  }

  private drawCreeps(graphics: Phaser.GameObjects.Graphics, creeps: Creep[], laneX: number, laneY: number, tileSize: number) {
    graphics.clear()

    for (const creep of creeps) {
      const px = laneX + creep.x * tileSize + tileSize / 2
      const py = laneY + creep.y * tileSize + tileSize / 2
      const s = tileSize / 40 // Scale factor
      const radius = Math.min(tileSize / 3, 12)

      // Calculate direction for facing
      let angle = 0
      if (creep.pathIndex < creep.path.length - 1) {
        const next = creep.path[creep.pathIndex + 1]
        angle = Math.atan2(next.y - creep.y, next.x - creep.x)
      }

      // Slow effect (ice aura)
      const isSlowed = creep.slowedUntil > Date.now()
      if (isSlowed) {
        graphics.fillStyle(0x4ad9d9, 0.25)
        graphics.fillCircle(px, py, radius + 6 * s)
        graphics.lineStyle(2 * s, 0x8aefef, 0.5)
        graphics.strokeCircle(px, py, radius + 4 * s)
      }

      // Draw creature based on type (sent units vs wave creeps)
      if (creep.isSent) {
        // Sent units - goblin-like creatures
        this.drawGoblinCreep(graphics, px, py, s, angle)
      } else {
        // Wave creeps - orc-like creatures
        this.drawOrcCreep(graphics, px, py, s, angle, creep.maxHp)
      }

      // Health bar with better styling
      const hpPercent = creep.hp / creep.maxHp
      const barWidth = tileSize * 0.7
      const barHeight = 4 * s
      const barX = px - barWidth / 2
      const barY = py - radius - 10 * s

      // Bar background
      graphics.fillStyle(0x1a1a1a, 0.8)
      graphics.fillRoundedRect(barX - 1, barY - 1, barWidth + 2, barHeight + 2, 2 * s)

      // Bar border
      graphics.lineStyle(1, 0x333333)
      graphics.strokeRoundedRect(barX - 1, barY - 1, barWidth + 2, barHeight + 2, 2 * s)

      // Health fill with gradient effect
      const hpColor = hpPercent > 0.6 ? 0x44dd44 : hpPercent > 0.3 ? 0xdddd44 : 0xdd4444
      graphics.fillStyle(hpColor)
      graphics.fillRoundedRect(barX, barY, barWidth * hpPercent, barHeight, 1.5 * s)

      // Health bar shine
      graphics.fillStyle(0xffffff, 0.3)
      graphics.fillRect(barX, barY, barWidth * hpPercent, barHeight / 2)
    }
  }

  private drawGoblinCreep(graphics: Phaser.GameObjects.Graphics, px: number, py: number, s: number, angle: number) {
    // Body (oval shape)
    graphics.fillStyle(0x6b8e23)
    graphics.fillEllipse(px, py, 14 * s, 12 * s)

    // Body shading
    graphics.fillStyle(0x556b2f, 0.6)
    graphics.fillEllipse(px, py + 2 * s, 12 * s, 8 * s)

    // Ears
    graphics.fillStyle(0x6b8e23)
    graphics.fillTriangle(
      px - 8 * s, py - 4 * s,
      px - 12 * s, py - 12 * s,
      px - 4 * s, py - 6 * s
    )
    graphics.fillTriangle(
      px + 8 * s, py - 4 * s,
      px + 12 * s, py - 12 * s,
      px + 4 * s, py - 6 * s
    )

    // Eyes (white)
    graphics.fillStyle(0xffffff)
    graphics.fillCircle(px - 3 * s, py - 2 * s, 3 * s)
    graphics.fillCircle(px + 3 * s, py - 2 * s, 3 * s)

    // Pupils (looking in direction of movement)
    const lookX = Math.cos(angle) * 1.2 * s
    const lookY = Math.sin(angle) * 1.2 * s
    graphics.fillStyle(0x1a1a1a)
    graphics.fillCircle(px - 3 * s + lookX, py - 2 * s + lookY, 1.5 * s)
    graphics.fillCircle(px + 3 * s + lookX, py - 2 * s + lookY, 1.5 * s)

    // Angry eyebrows
    graphics.lineStyle(1.5 * s, 0x3a5a2a)
    graphics.lineBetween(px - 6 * s, py - 6 * s, px - 1 * s, py - 4 * s)
    graphics.lineBetween(px + 6 * s, py - 6 * s, px + 1 * s, py - 4 * s)

    // Nose
    graphics.fillStyle(0x5a7a1a)
    graphics.fillCircle(px, py + 1 * s, 2 * s)

    // Mouth
    graphics.lineStyle(1 * s, 0x3a3a2a)
    graphics.beginPath()
    graphics.arc(px, py + 4 * s, 3 * s, 0.2, Math.PI - 0.2)
    graphics.strokePath()
  }

  private drawOrcCreep(graphics: Phaser.GameObjects.Graphics, px: number, py: number, s: number, angle: number, maxHp: number) {
    // Larger, tankier look for wave creeps
    const size = Math.min(1 + maxHp / 200, 1.5) // Scale with HP

    // Body (more muscular oval)
    graphics.fillStyle(0x8b4513)
    graphics.fillEllipse(px, py, 16 * s * size, 14 * s * size)

    // Armor plating
    graphics.fillStyle(0x4a3a2a)
    graphics.fillEllipse(px, py + 1 * s, 14 * s * size, 10 * s * size)

    // Shoulder pads
    graphics.fillStyle(0x5a4a3a)
    graphics.fillCircle(px - 7 * s * size, py, 4 * s * size)
    graphics.fillCircle(px + 7 * s * size, py, 4 * s * size)

    // Metal studs on armor
    graphics.fillStyle(0x888888)
    graphics.fillCircle(px - 4 * s, py + 2 * s, 1.2 * s)
    graphics.fillCircle(px + 4 * s, py + 2 * s, 1.2 * s)
    graphics.fillCircle(px, py + 4 * s, 1.2 * s)

    // Eyes (glowing red)
    graphics.fillStyle(0xff4444)
    graphics.fillCircle(px - 4 * s, py - 3 * s, 2.5 * s)
    graphics.fillCircle(px + 4 * s, py - 3 * s, 2.5 * s)

    // Eye glow
    graphics.fillStyle(0xffaaaa, 0.4)
    graphics.fillCircle(px - 4 * s, py - 3 * s, 4 * s)
    graphics.fillCircle(px + 4 * s, py - 3 * s, 4 * s)

    // Dark pupils
    const lookX = Math.cos(angle) * 0.8 * s
    const lookY = Math.sin(angle) * 0.8 * s
    graphics.fillStyle(0x2a0000)
    graphics.fillCircle(px - 4 * s + lookX, py - 3 * s + lookY, 1 * s)
    graphics.fillCircle(px + 4 * s + lookX, py - 3 * s + lookY, 1 * s)

    // Tusks
    graphics.fillStyle(0xeeeecc)
    graphics.fillTriangle(
      px - 6 * s, py + 2 * s,
      px - 3 * s, py + 2 * s,
      px - 5 * s, py + 8 * s
    )
    graphics.fillTriangle(
      px + 6 * s, py + 2 * s,
      px + 3 * s, py + 2 * s,
      px + 5 * s, py + 8 * s
    )

    // Helmet ridge
    graphics.fillStyle(0x3a2a1a)
    graphics.fillRect(px - 2 * s, py - 8 * s * size, 4 * s, 6 * s)
  }

  private drawProjectiles() {
    this.projectileGraphics.clear()

    for (const proj of this.projectiles) {
      const age = Date.now() - proj.createdAt
      const progress = Math.min(age / 100, 1) // Projectile travel animation

      // Calculate current position along path
      const currentX = proj.fromX + (proj.toX - proj.fromX) * progress
      const currentY = proj.fromY + (proj.toY - proj.fromY) * progress

      // Calculate angle for directional projectiles
      const angle = Math.atan2(proj.toY - proj.fromY, proj.toX - proj.fromX)

      switch (proj.towerType) {
        case 'arrow':
          this.drawArrowProjectile(currentX, currentY, angle)
          break
        case 'cannon':
          this.drawCannonballProjectile(currentX, currentY, progress)
          break
        case 'frost':
          this.drawIceShardProjectile(currentX, currentY, angle)
          break
        case 'sniper':
          this.drawBulletProjectile(proj.fromX, proj.fromY, proj.toX, proj.toY, progress)
          break
      }

      // Impact effect when projectile lands
      if (progress >= 0.9) {
        this.drawImpactEffect(proj.toX, proj.toY, proj.towerType)
      }
    }
  }

  private drawArrowProjectile(x: number, y: number, angle: number) {
    const g = this.projectileGraphics

    // Arrow shaft
    g.lineStyle(2, 0x8b4513)
    g.lineBetween(
      x - Math.cos(angle) * 12,
      y - Math.sin(angle) * 12,
      x + Math.cos(angle) * 4,
      y + Math.sin(angle) * 4
    )

    // Arrow head
    g.fillStyle(0xcccccc)
    g.fillTriangle(
      x + Math.cos(angle) * 8,
      y + Math.sin(angle) * 8,
      x + Math.cos(angle + 2.5) * 4,
      y + Math.sin(angle + 2.5) * 4,
      x + Math.cos(angle - 2.5) * 4,
      y + Math.sin(angle - 2.5) * 4
    )

    // Fletching
    g.fillStyle(0xff4444, 0.8)
    g.fillTriangle(
      x - Math.cos(angle) * 10,
      y - Math.sin(angle) * 10,
      x - Math.cos(angle) * 14 + Math.cos(angle + 1.5) * 4,
      y - Math.sin(angle) * 14 + Math.sin(angle + 1.5) * 4,
      x - Math.cos(angle) * 14 + Math.cos(angle - 1.5) * 4,
      y - Math.sin(angle) * 14 + Math.sin(angle - 1.5) * 4
    )
  }

  private drawCannonballProjectile(x: number, y: number, progress: number) {
    const g = this.projectileGraphics

    // Cannonball with arc (simulate gravity)
    const arcHeight = Math.sin(progress * Math.PI) * 15

    // Shadow
    g.fillStyle(0x000000, 0.3)
    g.fillEllipse(x, y + 8, 10, 4)

    // Cannonball
    g.fillStyle(0x2a2a2a)
    g.fillCircle(x, y - arcHeight, 6)

    // Highlight
    g.fillStyle(0x4a4a4a)
    g.fillCircle(x - 2, y - arcHeight - 2, 2)

    // Trail smoke
    g.fillStyle(0x666666, 0.3)
    g.fillCircle(x - 8, y - arcHeight + 2, 4)
    g.fillStyle(0x888888, 0.2)
    g.fillCircle(x - 14, y - arcHeight + 4, 3)
  }

  private drawIceShardProjectile(x: number, y: number, angle: number) {
    const g = this.projectileGraphics

    // Outer glow
    g.fillStyle(0x4ad9d9, 0.3)
    g.fillCircle(x, y, 10)

    // Ice crystal shape
    g.fillStyle(0x8aefef)
    g.fillTriangle(
      x + Math.cos(angle) * 10,
      y + Math.sin(angle) * 10,
      x + Math.cos(angle + 2.2) * 6,
      y + Math.sin(angle + 2.2) * 6,
      x + Math.cos(angle - 2.2) * 6,
      y + Math.sin(angle - 2.2) * 6
    )

    // Inner bright core
    g.fillStyle(0xffffff, 0.8)
    g.fillCircle(x, y, 3)

    // Sparkles
    g.fillStyle(0xffffff, 0.6)
    g.fillCircle(x + Math.cos(angle + 1) * 6, y + Math.sin(angle + 1) * 6, 1.5)
    g.fillCircle(x + Math.cos(angle - 1) * 6, y + Math.sin(angle - 1) * 6, 1.5)
  }

  private drawBulletProjectile(fromX: number, fromY: number, toX: number, toY: number, progress: number) {
    const g = this.projectileGraphics

    // Tracer line (instant hit feel)
    g.lineStyle(3, 0xffff44, 0.8 * (1 - progress))
    g.lineBetween(fromX, fromY, toX, toY)

    g.lineStyle(1, 0xffffff, 0.6 * (1 - progress))
    g.lineBetween(fromX, fromY, toX, toY)

    // Muzzle flash at origin
    if (progress < 0.3) {
      g.fillStyle(0xffff88, 0.6 * (1 - progress * 3))
      g.fillCircle(fromX, fromY, 8)
      g.fillStyle(0xffffff, 0.4 * (1 - progress * 3))
      g.fillCircle(fromX, fromY, 4)
    }
  }

  private drawImpactEffect(x: number, y: number, towerType: TowerType) {
    const g = this.projectileGraphics

    switch (towerType) {
      case 'arrow':
        // Small dust puff
        g.fillStyle(0xccaa88, 0.4)
        g.fillCircle(x, y, 6)
        break
      case 'cannon':
        // Explosion
        g.fillStyle(0xff6600, 0.5)
        g.fillCircle(x, y, 15)
        g.fillStyle(0xffaa00, 0.4)
        g.fillCircle(x, y, 10)
        g.fillStyle(0xffff00, 0.3)
        g.fillCircle(x, y, 5)
        break
      case 'frost':
        // Ice burst
        g.fillStyle(0x8aefef, 0.4)
        g.fillCircle(x, y, 12)
        g.lineStyle(2, 0xffffff, 0.5)
        g.strokeCircle(x, y, 8)
        break
      case 'sniper':
        // Small spark
        g.fillStyle(0xffff00, 0.6)
        g.fillCircle(x, y, 4)
        g.fillStyle(0xffffff, 0.4)
        g.fillCircle(x, y, 2)
        break
    }
  }

  private drawPlacementPreview() {
    this.uiOverlayGraphics.clear()

    if (!this.selectedTowerType || !this.hoverTile) return

    const canPlace = this.canPlaceTowerAt(this.hoverTile.x, this.hoverTile.y)
    const color = canPlace ? 0x00ff00 : 0xff0000

    const px = this.PLAYER_LANE_X + this.hoverTile.x * this.PLAYER_TILE_SIZE
    const py = this.PLAYER_LANE_Y + this.hoverTile.y * this.PLAYER_TILE_SIZE

    // Highlight tile
    this.uiOverlayGraphics.fillStyle(color, 0.3)
    this.uiOverlayGraphics.fillRect(px, py, this.PLAYER_TILE_SIZE - 1, this.PLAYER_TILE_SIZE - 1)

    this.uiOverlayGraphics.lineStyle(2, color, 0.8)
    this.uiOverlayGraphics.strokeRect(px, py, this.PLAYER_TILE_SIZE - 1, this.PLAYER_TILE_SIZE - 1)

    // Range circle
    if (canPlace) {
      const def = TOWERS[this.selectedTowerType]
      const centerX = px + this.PLAYER_TILE_SIZE / 2
      const centerY = py + this.PLAYER_TILE_SIZE / 2

      this.uiOverlayGraphics.lineStyle(1, color, 0.4)
      this.uiOverlayGraphics.strokeCircle(centerX, centerY, def.range * this.PLAYER_TILE_SIZE)

      this.uiOverlayGraphics.fillStyle(color, 0.1)
      this.uiOverlayGraphics.fillCircle(centerX, centerY, def.range * this.PLAYER_TILE_SIZE)
    }
  }

  private checkWaveEnd() {
    if (!this.isWaveActive) return
    if (this.creepsToSpawn > 0) return
    if (this.playerCreeps.length > 0) return

    // Check if any enemy lane still has creeps to spawn or active creeps
    for (const [enemyId, enemy] of this.enemies) {
      const enemyCreepsToSpawn = this.enemyCreepsToSpawn.get(enemyId) || 0
      if (enemyCreepsToSpawn > 0 || enemy.creeps.length > 0) return
    }

    this.isWaveActive = false
    this.timerText.setText('WAVE COMPLETE')
    this.timerText.setColor('#88ff88')

    // Check if game can continue (player and at least one enemy alive)
    const anyEnemyAlive = Array.from(this.enemies.values()).some(e => e.lives > 0)

    this.time.delayedCall(1500, () => {
      if (this.lives > 0 && anyEnemyAlive) {
        this.startBuildPhase()
      }
    })
  }

  private checkGameOver() {
    if (this.lives <= 0) {
      this.scene.start('GameOverScene', { winner: 'enemy', wave: this.currentWave, stats: this.stats })
      return
    }

    // Check if all enemies are eliminated
    const allEnemiesDead = this.enemies.size > 0 && Array.from(this.enemies.values()).every(e => e.lives <= 0)
    if (allEnemiesDead) {
      this.scene.start('GameOverScene', { winner: 'player', wave: this.currentWave, stats: this.stats })
    }
  }
}
